import styled from 'styled-components';
import { Sizes } from '../../utils/sizes';
import { Colors } from '../../utils/colors';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #0a0a0a;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,215,0,0.2), transparent);
  }
`;

export const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 48px 24px 80px;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    padding: 32px 16px 60px;
  }
`;

export const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 56px;
  align-items: start;

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    grid-template-columns: 320px 1fr;
    gap: 40px;
  }

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

export const ProductImage = styled.div`
  width: 100%;
  aspect-ratio: 3/4;
  border-radius: 14px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 24px 70px rgba(0,0,0,0.6);
  border: 1px solid rgba(255,215,0,0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    max-width: 320px;
    margin: 0 auto;
  }
`;

export const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-top: 4px;
`;

export const ProductTitle = styled.h1`
  color: #fff;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 900;
  line-height: 1.2;
  margin: 0;
`;

export const PriceSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

export const PriceTag = styled.span`
  color: ${Colors.yellow};
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  font-weight: 900;
  letter-spacing: -1px;
  white-space: nowrap;
`;

export const OriginalPrice = styled.span`
  color: rgba(255,255,255,0.3);
  font-size: 1rem;
  text-decoration: line-through;
`;

export const Discount = styled.span`
  background: ${Colors.yellow};
  color: #0a0a0a;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.5px;
`;

export const Description = styled.p`
  color: rgba(255,255,255,0.65);
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: ${Sizes.mediaQueryBreak3}) {
    flex-direction: column;
  }
`;

export const Button = styled.button`
  flex: 1;
  padding: 15px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border: none;

  &.primary {
    background: ${Colors.yellow};
    color: #0a0a0a;

    &:hover {
      background: #ffe033;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(255,215,0,0.3);
    }
  }

  &.secondary {
    background: transparent;
    color: rgba(255,255,255,0.8);
    border: 1px solid rgba(255,255,255,0.2);

    &:hover {
      border-color: ${Colors.yellow};
      color: ${Colors.yellow};
      background: rgba(255,215,0,0.05);
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const FullDescription = styled.div`
  background: #111;
  border: 1px solid rgba(255,215,0,0.1);
  border-radius: 10px;
  padding: 24px;
  font-size: 0.88rem;
  line-height: 1.6;

  h3 {
    color: ${Colors.yellow};
    font-size: 0.95rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 12px;
  }

  h4 {
    color: rgba(255,255,255,0.7);
    font-size: 0.85rem;
    font-weight: 700;
    margin: 20px 0 8px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      color: rgba(255,255,255,0.55);
      padding: 6px 0;
      border-bottom: 1px solid rgba(255,255,255,0.05);
      display: flex;
      align-items: center;
      gap: 8px;

      &::before {
        content: '♪';
        color: rgba(255,215,0,0.4);
        font-size: 10px;
        flex-shrink: 0;
      }

      &:last-child { border-bottom: none; }
    }
  }
`;

export const RelatedProducts = styled.section`
  margin-top: 80px;
  padding-top: 48px;
  border-top: 1px solid rgba(255,215,0,0.1);
`;

export const RelatedTitle = styled.h2`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 800;
  margin: 0 0 28px;
  text-transform: uppercase;
  letter-spacing: 1px;

  span { color: ${Colors.yellow}; }
`;

export const NotFoundTitle = styled.h1`
  color: rgba(255,255,255,0.5);
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 80px 0;
`;

export const RelatedProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: ${Sizes.mediaQueryBreak3}) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

export const ViewMoreContainer = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 28px;
  background: transparent;
  border: 1px solid rgba(255,215,0,0.25);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  margin: 0 auto;

  &:hover {
    border-color: ${Colors.yellow};
    background: rgba(255,215,0,0.05);

    .arrow { transform: translateX(4px); }
  }

  .text {
    color: rgba(255,255,255,0.6);
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .arrow {
    color: ${Colors.yellow};
    font-size: 16px;
    transition: transform 0.2s;
  }
`;
